import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import axios, { CancelTokenSource } from "axios";

import { API_URLS } from "src/utils/API_URLS";
import { RootState } from "src/redux/reducers";
import { wsAxiosGet } from "src/helpers/wsAxios";
import { ApiResponse } from "src/types/api";

export interface SearchHistoryItem {
  uuid: string;
  created: string;
  projectNameMetaFieldUuid: string;
  projectName: string | null;
}

type SearchHistoryResponse = ApiResponse<SearchHistoryItem[]>;

type UseGetSearchHistoryResult = {
  data: SearchHistoryResponse | null;
  isLoading: boolean;
};

/**
 * Custom hook to fetch search history for a workspace.
 *
 * This hook fetches the search history from the API using the provided fromDate parameter.
 * It automatically retrieves the workspace UUID from Redux and handles request cancellation
 * on component unmount to prevent memory leaks.
 *
 * @param {string} fromDate - The date from which to fetch search history (string format)
 * @returns {UseGetSearchHistoryResult} The state object containing the fetch status and data
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useGetSearchHistory('2024-01-01');
 *
 * useEffect(() => {
 *   if (data?.content) {
 *     console.log('Search History:', data.content);
 *   }
 * }, [data]);
 * ```
 */
export const useGetSearchHistory = (
  fromDate: string,
): UseGetSearchHistoryResult => {
  const [data, setData] = useState<SearchHistoryResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const activeWorkspaceUuid = useSelector(
    (state: RootState) => state.mainReducer.activeWorkspaceUuid,
  );
  const cancelTokenSource = useRef<CancelTokenSource | null>(null);

  const abort = () => {
    cancelTokenSource?.current?.cancel();
  };

  useEffect(() => {
    if (!activeWorkspaceUuid || !fromDate) {
      return;
    }

    abort();
    cancelTokenSource.current = axios.CancelToken.source();

    const fetchSearchHistory = async () => {
      setIsLoading(true);
      try {
        const url = API_URLS.getSearchHistory
          .replace(":wsWorkspaceUuid:", activeWorkspaceUuid)
          .replace(":fromDate:", fromDate);

        const response = await wsAxiosGet<SearchHistoryResponse>({
          url,
          config: {
            cancelToken: cancelTokenSource.current?.token,
          },
        });

        if (response) {
          setData(response);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchSearchHistory();
  }, [activeWorkspaceUuid, fromDate]);

  useEffect(() => {
    return () => {
      abort();
    };
  }, []);

  return { data, isLoading };
};
